import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { University, Department } from '../../../services/commonService';

interface SelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: (University | Department)[];
  selectedItem: University | Department | null;
  onSelect: (item: University | Department) => void;
  isLoading?: boolean;
}

const SearchContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ListContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const ListItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  border: none;
  border-bottom: 1px solid ${props => props.theme.colors.gray[100]};
  background-color: ${props => props.$isSelected ? props.theme.colors.primary[50] : props.theme.colors.white};
  color: ${props => props.theme.colors.gray[900]};
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const EmptyMessage = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSize.sm};
`;

const LoadingMessage = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSize.sm};
`;

const SelectionModal: React.FC<SelectionModalProps> = ({
  isOpen,
  onClose,
  title,
  items,
  selectedItem,
  onSelect,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (item: University | Department) => {
    onSelect(item);
    onClose();
  };

  const isUniversity = (item: University | Department): item is University => {
    return 'department_no' in item === false;
  };

  const isDepartment = (item: University | Department): item is Department => {
    return 'department_no' in item === true;
  };

  const getItemKey = (item: University | Department): number => {
    if ('department_no' in item) {
      return item.department_no;
    }
    return item.university_no;
  };

  const isItemSelected = (item: University | Department): boolean => {
    if (!selectedItem) return false;
    
    if (isUniversity(item) && isUniversity(selectedItem)) {
      return item.university_no === selectedItem.university_no;
    }
    
    if (isDepartment(item) && isDepartment(selectedItem)) {
      return item.department_no === selectedItem.department_no;
    }
    
    return false;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <SearchContainer>
        <Input
          placeholder="검색..."
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </SearchContainer>
      <ListContainer>
        {isLoading ? (
          <LoadingMessage>로딩 중...</LoadingMessage>
        ) : filteredItems.length === 0 ? (
          <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
        ) : (
          filteredItems.map((item) => (
            <ListItem
              key={getItemKey(item)}
              $isSelected={isItemSelected(item)}
              onClick={() => handleSelect(item)}
            >
              {item.name}
            </ListItem>
          ))
        )}
      </ListContainer>
    </Modal>
  );
};

export default SelectionModal;
